import {Component, EventEmitter, inject, Input, Output} from '@angular/core';
import {ObjectDeletionService} from '../../core/object-deletion.service';
import {UserDialogsService} from '../user-dialogs.service';
import {BaseModel} from '../../core/definitions/base-model';
import {CommonsService} from '../../core/commons.service';

@Component({
    selector: 'app-delete-item-button',
    templateUrl: './delete-item-button.component.html',
    styleUrls: ['./delete-item-button.component.scss'],
    standalone: false
})
export class DeleteItemButtonComponent {
  // Injections
  private readonly commons: CommonsService = inject(CommonsService);
  private readonly objectDeletionService: ObjectDeletionService = inject(ObjectDeletionService);
  private readonly userDialogService: UserDialogsService = inject(UserDialogsService);

  @Input() rootObject: any;
  @Input() parentObject: any;
  @Input() fieldName: string;
  @Input() item: BaseModel;
  @Input() deleteFromStore: boolean;
  @Input() checkUsage: boolean;
  @Input() deleteTargetModel: any;
  @Input() deleteTargetModelArrayName: string;
  @Input() deleteContextMapParentToChild: Array<string>;

  @Output() callback = new EventEmitter<void>();

  error = null;

  private deleteArtifact(fn) {
    this.objectDeletionService.deleteObject(this.item).then(
      () => {
        fn.success();
      },
      message => {
        fn.error('Failed: ' + message);
      }
    );
  }

  private detachImage(fn) {
    if (this.parentObject) {
      this.objectDeletionService.detachImagesFromObject(this.parentObject, [this.item],
        () => {
          fn.success();
        },
        error => {
          fn.error('Detaching images failed with message ' + error);
        });
    } else {
      throw new Error('A parent must be present in order to delete images');
    }
  }

  private itemsEqual(item1, item2) {
    const res = {
      equal: false,
      err: null
    };
    if (item1.order_number === undefined) {
      res.err =
        'Missing order number for ' + this.fieldName;
    } else {
      res.equal =
        item1.order_number === item2.order_number;
    }
    return res;
  }

  private deleteArrayItem(fn) {
    let itemIndex = -1, err, index, res;
    const array = this.parentObject[this.fieldName];

    if (!this.rootObject) {
      err = 'Root model must be set in order to delete';
    } else {
      for (index = 0; index < array.length; index++) {
        res = this.itemsEqual(array[index], this.item);
        if (res.equal) {
          itemIndex = index;
          break;
        }
        err = res.err;
      }
      if (itemIndex !== -1) {
        this.objectDeletionService.deleteItem(this.rootObject, this.fieldName, itemIndex);
        if (this.deleteFromStore) {
          this.deleteArtifact(fn);
        } else {
          fn.success();
        }
      } else {
        err = 'Unable to find item in array ';
      }
    }
    if (err) {
      fn.error(err);
    }
  }

  private deleteIt(fn) {
    if (this.fieldName) {
      if (this.deleteTargetModel) {
        this.objectDeletionService.deleteFromTargetModel(
          this.parentObject,
          this.fieldName,
          this.item,
          this.deleteTargetModel,
          this.deleteTargetModelArrayName,
          this.deleteContextMapParentToChild).then(() => {
            fn.success();
          },
          reason => {
            fn.error('Deletion failed: ' + reason.error.message);
          });
      } else {
        this.deleteArrayItem(fn);
      }

    } else {
      if (this.item.object_type !== 'Image') {
        this.deleteArtifact(fn);
      } else {
        this.detachImage(fn);
      }
    }
  }

  private deleteTheItem() {
    this.deleteIt({
      success: () => {
        if (this.callback) {
          this.callback.emit();
        }
      },
      error: (errMsg) => {
        this.error = {
          message: 'TRANS__DELETE_ITEM__ERROR_DELETING'
        };
        console.warn(errMsg);
      }
    });
  }

  confirmDelete() {
    const idField = this.commons.getObjectIdField(this.item);
    if (this.checkUsage && this.item[idField]) {
      this.userDialogService.showObjectUsage(this.item[idField], true,
        'TRANS__DELETE_ITEM__CANNOT_DELETE_HAS_USAGES').then(usage => {
        if (!usage) {
          this.deleteTheItem();
        }
      });
    } else {
      this.deleteTheItem();
    }
  }
}
